import { ArrowRightIcon, TriangleAlert } from 'lucide-react';

import { Button, Pill } from '@/components/ui';
import { env } from '@/config/env';
import { openLiveSessionInOldApp } from '@/features/modules';
import { useCountdown } from '@/hooks/use-countdown';
import { useAnalytics } from '@/lib/analytics';
import {
  SESSION_CARD_VARIANTS,
  type SessionCardVariant,
} from '@/lib/feature-flags';
import { cn, getSessionLabel } from '@/lib/utils';
import { useNotifications } from '@/stores/notifications';
import { useSchedulePageStore } from '@/stores/schedule-page';

import { UnifiedSession } from '../../types';
import { getSessionDisplayName } from '../../utils/session-helpers';

import hobbyClassImage from './assets/hobby-class.png';
import masterClassImage from './assets/master-class.png';
import { UpcomingSessionCardDiv } from './upcoming-session-card-div';
import {
  SessionCardSkeletonContent,
  SessionCardEmptyContent,
} from './upcoming-session-card-states';

const SESSION_LABEL = getSessionLabel();

/**
 * In seconds
 * To compare against timeLeft (in seconds)
 */
const MIN_THRESHOLD = env.IS_OFFLINE ? 0 : 5 * 60;

const EXTRA_CLASS_IMAGE_CONFIG = {
  hobby_class: {
    image: hobbyClassImage,
    alt: 'hobby class',
    firstCard: '-bottom-1.5 right-0 w-23 h-23 md:w-28 md:h-28',
    secondCard: '-bottom-1.5 right-0 w-23 h-23 md:w-25 md:h-25',
  },
  master_class: {
    image: masterClassImage,
    alt: 'master class',
    firstCard: 'bottom-0 -right-5.5 w-28 h-28 md:w-32 md:h-32',
    secondCard: 'bottom-0 -right-4 w-24 h-24 md:w-24 md:h-24',
  },
} as const;

function CreditsNotConsumedMessage() {
  return (
    <span className="text-sm text-white/80">
      Your credits have not been consumed
    </span>
  );
}

function SessionTiming({
  isCancelled,
  countdown,
}: Readonly<{ isCancelled: boolean; countdown: string }>) {
  if (isCancelled) return <CreditsNotConsumedMessage />;

  return (
    <>
      <span className="text-sm text-white/80">
        {SESSION_LABEL.capitalize} Starts in
      </span>
      <span className="text-xl font-bold">{countdown}</span>
    </>
  );
}

function scheduleClassName(
  isSecondSession: boolean | undefined,
  isCancelled: boolean
) {
  return cn(
    isSecondSession ? 'text-2xl' : 'text-3xl',
    'font-bold',
    isCancelled && 'line-through'
  );
}

export default function UpcomingSessionCard({
  session,
  isLoading,
  isError,
  isSecondSession,
  variant = SESSION_CARD_VARIANTS.DEFAULT,
  showTrialTag = false,
}: Readonly<{
  session: UnifiedSession | undefined;
  isLoading: boolean;
  isError: boolean;
  isSecondSession?: boolean;
  variant?: SessionCardVariant;
  showTrialTag?: boolean;
}>) {
  const { addNotification } = useNotifications();

  const remainingSeconds = session?.remaining_seconds ?? 0;
  const { countdown, timeLeft } = useCountdown(remainingSeconds);

  const { setIsOpen, setFilter } = useSchedulePageStore();

  const { trackEvent } = useAnalytics();

  const isExtraClass = session?.isExtraClass ?? false;
  const isCancelled = session?.session_status === 'cancelled';

  const containerProps = {
    isSecondSession,
    variant,
    showTrialTag,
    trialSessionNumber: showTrialTag ? session?.number : undefined,
    isCancelled,
  };

  const handleSecondSessionClick = () => {
    if (isExtraClass) {
      if (session?.classType === 'hobby_class') {
        setFilter(
          { variant: session?.classType ?? 'hobby_class' },
          { force: true }
        );
        setIsOpen(true);
      } else if (session?.classType === 'master_class') {
        if (session.joining_link) {
          window.open(session.joining_link, '_blank');
        } else {
          addNotification({
            title: 'Master class joining link not found.',
            type: 'error',
          });
        }
      }
    }
  };

  if (isLoading) {
    return (
      <UpcomingSessionCardDiv {...containerProps}>
        <SessionCardSkeletonContent isSecondSession={isSecondSession} />
      </UpcomingSessionCardDiv>
    );
  }

  if (isError || !session) {
    return (
      <UpcomingSessionCardDiv
        {...containerProps}
        onClick={isSecondSession ? handleSecondSessionClick : undefined}
      >
        <SessionCardEmptyContent isSecondSession={isSecondSession} />
      </UpcomingSessionCardDiv>
    );
  }

  const sessionDay = session?.readable_day;
  const sessionTime = session?.readable_time;
  const displayName = getSessionDisplayName(session);

  const getSessionHref = () => {
    if (session.platform === 'ivs') {
      return session.session_id
        ? `/maverick/class/${session.session_id}`
        : '/maverick';
    } else {
      return `${session?.link}&source=sd`;
    }
  };

  const onClickJoinSession = () => {
    trackEvent('clickedJoinSession', 1, {
      ...(session?.timestamp && {
        sessionDateTime: new Date(session.timestamp * 1000).toISOString(),
      }),
    });
    if (session?.delivery_type === 'recorded_stream' && session?.variant_id) {
      openLiveSessionInOldApp(session?.variant_id);
    } else {
      const href = getSessionHref();
      window.open(href, '_blank');
    }
  };

  const makeButton = () => {
    if (!env.IS_OFFLINE) {
      return (
        <div
          className={cn(
            'relative',
            !isExtraClass || !session?.classType ? 'w-full' : 'w-[70%]'
          )}
        >
          <Button
            variant="raised-jumbo"
            fullWidth
            icon={<ArrowRightIcon className="h-10" />}
            iconPosition="end"
            onClick={onClickJoinSession}
          >
            Join {SESSION_LABEL.capitalize}
          </Button>
        </div>
      );
    }

    return (
      <div className="bg-white/10 rounded-xl p-4">
        <div className="flex items-center gap-2">
          <span className="text-2xl font-bold text-white/80">
            {SESSION_LABEL.capitalize} Started
          </span>
        </div>
      </div>
    );
  };

  const renderExtraClassImage = () => {
    if (!isExtraClass || !session?.classType) return null;

    const config = EXTRA_CLASS_IMAGE_CONFIG[session.classType];
    const positionClasses = isSecondSession
      ? config.secondCard
      : config.firstCard;

    return (
      <img
        src={config.image}
        alt={config.alt}
        className={`absolute ${positionClasses} object-contain pointer-events-none`}
      />
    );
  };

  return (
    <UpcomingSessionCardDiv
      {...containerProps}
      onClick={isSecondSession ? handleSecondSessionClick : undefined}
    >
      {renderExtraClassImage()}

      {!isSecondSession && !isCancelled && (
        <div className="text-sm font-medium text-white/40">
          UPCOMING {SESSION_LABEL.uppercase}
        </div>
      )}
      {isCancelled ? (
        <Pill
          variant="error"
          size="md"
          className="flex items-center bg-[#DD2138] text-shadow-accent max-w-fit"
        >
          <TriangleAlert className="size-4 shrink-0" aria-hidden />
          Cancelled
        </Pill>
      ) : null}

      <div className="flex items-center gap-4">
        <Pill rounded>{session?.readable_date}</Pill>
        {isExtraClass ? (
          <Pill rounded maxWidth="48">
            {displayName}
          </Pill>
        ) : (
          <Pill rounded maxWidth="48">
            {env.IS_OFFLINE
              ? `${SESSION_LABEL.capitalize} ${session?.number}`
              : `${session?.subject ?? ''} ${SESSION_LABEL.capitalize}`}
          </Pill>
        )}
      </div>

      <div className="flex items-center gap-8">
        <div className={scheduleClassName(isSecondSession, isCancelled)}>
          {sessionDay}
        </div>
        <div className={scheduleClassName(isSecondSession, isCancelled)}>
          {sessionTime}
        </div>
      </div>

      {isSecondSession ? null : (
        <>
          <div className="flex-1"></div>
          {timeLeft > MIN_THRESHOLD || isCancelled ? (
            <div className="bg-white/10 rounded-xl p-4">
              <div className="flex items-center gap-2">
                <SessionTiming
                  isCancelled={isCancelled}
                  countdown={countdown}
                />
              </div>
            </div>
          ) : (
            makeButton()
          )}
        </>
      )}
    </UpcomingSessionCardDiv>
  );
}
